/**
 * The Seat Assignment session (ESAPA-104 / mapd-issues#663).
 *
 * The module, the realtime bridge and the page lifecycle were all built and
 * tested, and nothing owned them. This is the owner: one `SeatAssignmentModule`
 * for the admin's session, with the bridge and the lifecycle hooks attached to
 * that one instance, so there is one seat writer per session rather than one
 * per feature.
 *
 * It is a session rather than a React value on purpose. Gala Seating's panel
 * mounts and unmounts as the admin moves around the editor, and a module per
 * mount would mean a fresh fetch, a second set of socket listeners and pending
 * writes dropped on the floor every time. Views subscribe and unsubscribe; the
 * session outlives them.
 */

import { SeatAssignmentModule } from './SeatAssignmentModule';
import { attachRealtimeBridge, RealtimeSource } from './realtimeBridge';
import { attachSeatAssignmentLifecycle, LifecycleTargets } from './lifecycle';
import { RemoteMove, SeatAssignmentError, SeatBackend } from './types';

/** A view listening to the session. Every callback is optional. */
export interface SeatSessionSubscriber {
  /** The seating model changed — re-render. */
  onChange?: () => void;
  /** Something the admin has to be told about, rather than swallowed. */
  onError?: (error: SeatAssignmentError) => void;
  /** A Remote Change moved an attendee the admin may be looking at. */
  onRemoteMove?: (move: RemoteMove) => void;
}

export interface SeatSession {
  readonly eventId: string;
  readonly module: SeatAssignmentModule;
  /** Listen to the session. Returns an unsubscriber. */
  subscribe(subscriber: SeatSessionSubscriber): () => void;
  /**
   * Fetch the Seat Assignments once for this session. Repeat calls await the
   * same load; a load that failed is retried by the next call, since without
   * one the module holds no assignments at all.
   */
  ensureLoaded(): Promise<void>;
  /** Flush pending writes and detach every listener. */
  dispose(): void;
}

export interface SeatSessionOptions {
  eventId: string;
  backend: SeatBackend;
  /** Coalescing window for outbound writes. The module's default otherwise. */
  coalesceWindowMs?: number;
  /** Where the realtime events arrive. Defaults to `window`. */
  source?: RealtimeSource;
  /** Where the page lifecycle events arrive. Defaults to document/window. */
  lifecycleTargets?: LifecycleTargets;
  /**
   * Whether the realtime socket is already up. The session is created when the
   * admin opens a Design, by which time the socket's `connect` has long since
   * fired, so without this the module would report a healthy channel as never
   * connected for the whole session (mapd-issues#664).
   */
  isConnected?: () => boolean;
}

export function createSeatSession(options: SeatSessionOptions): SeatSession {
  const subscribers = new Set<SeatSessionSubscriber>();

  const fanOut = <K extends keyof SeatSessionSubscriber>(
    key: K,
    ...args: Parameters<NonNullable<SeatSessionSubscriber[K]>>
  ): void => {
    // Copied first: a subscriber is free to unsubscribe from its own callback.
    [...subscribers].forEach((subscriber) => {
      const handler = subscriber[key] as ((...a: any[]) => void) | undefined;
      handler?.(...args);
    });
  };

  const module = new SeatAssignmentModule({
    backend: options.backend,
    coalesceWindowMs: options.coalesceWindowMs,
    onChange: () => fanOut('onChange'),
    onError: (error) => fanOut('onError', error),
    onRemoteMove: (move) => fanOut('onRemoteMove', move),
  });

  const detachBridge = attachRealtimeBridge({
    module,
    source: options.source,
    eventId: options.eventId,
    isConnected: options.isConnected,
  });
  const detachLifecycle = attachSeatAssignmentLifecycle(module, options.lifecycleTargets);

  let loading: Promise<void> | null = null;

  return {
    eventId: options.eventId,
    module,

    subscribe(subscriber: SeatSessionSubscriber): () => void {
      subscribers.add(subscriber);
      return () => {
        subscribers.delete(subscriber);
      };
    },

    ensureLoaded(): Promise<void> {
      if (!loading) {
        loading = module.load().catch((error: unknown) => {
          // Let the next caller try again — a room with no Seat Assignments in
          // it is not a room the admin can trust.
          loading = null;
          fanOut('onError', {
            kind: 'load-failed',
            message: `Seat Assignments could not be loaded: ${
              error instanceof Error ? error.message : String(error)
            }`,
          });
        });
      }
      return loading;
    },

    dispose(): void {
      detachBridge();
      detachLifecycle();
      subscribers.clear();
      void module.flushPending();
    },
  };
}

// -----------------------------------------------------------------------------
// The one session Gala Seating uses
// -----------------------------------------------------------------------------

let active: SeatSession | null = null;

/**
 * The session for an event, created on first use and shared by everything that
 * touches seating afterwards. Asking for a different event replaces it, so
 * switching events does not leave the previous event's writer listening.
 */
export function seatSessionFor(options: SeatSessionOptions): SeatSession {
  if (active && active.eventId === options.eventId) return active;
  if (active) active.dispose();
  active = createSeatSession(options);
  return active;
}

/** Drop the active session. Used when a session genuinely ends, and by tests. */
export function resetSeatSession(): void {
  active?.dispose();
  active = null;
}
